import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { clienteService } from '../../services/api';

type ClienteForm = {
  nome: string;
  email: string;
  celular: string;
  telefone: string;
  cpf: string;
  rg: string;
  endereco: string;
  bairro: string;
  cidade: string;
  observacoes: string;
  ativo: boolean;
};

const ClienteEditar: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const [form, setForm] = useState<ClienteForm>({
    nome: '',
    email: '',
    celular: '',
    telefone: '',
    cpf: '',
    rg: '',
    endereco: '',
    bairro: '',
    cidade: '',
    observacoes: '',
    ativo: true
  });

  const pick = (obj: any, ...keys: string[]) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null) return v;
    }
    return undefined;
  };

  const carregarCliente = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');

      const response = await clienteService.buscarPorId(id);
      const dados = response?.data?.data || response?.data;

      if (!dados) {
        setError('Cliente não encontrado');
        return;
      }

      setForm({
        nome: String(pick(dados, 'nome') || ''),
        email: String(pick(dados, 'email') || ''),
        celular: String(pick(dados, 'celular') || ''),
        telefone: String(pick(dados, 'telefone') || ''),
        cpf: String(pick(dados, 'cpf') || ''),
        rg: String(pick(dados, 'rg') || ''),
        endereco: String(pick(dados, 'endereco') || ''),
        bairro: String(pick(dados, 'bairro') || ''),
        cidade: String(pick(dados, 'cidade') || ''),
        observacoes: String(pick(dados, 'observacoes') || ''),
        ativo: Boolean(pick(dados, 'ativo') ?? true)
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao carregar cliente');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarCliente();
  }, [id]);

  const handleChange = (key: keyof ClienteForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = key === 'ativo' ? (e.target as any).checked : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSalvar = async () => {
    if (!id) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        nome: form.nome,
        email: form.email,
        celular: form.celular,
        telefone: form.telefone,
        cpf: form.cpf,
        rg: form.rg,
        endereco: form.endereco,
        bairro: form.bairro,
        cidade: form.cidade,
        observacoes: form.observacoes,
        ativo: form.ativo
      };

      const resp = await clienteService.atualizar(id, payload);
      if (resp?.data?.success === false) {
        setError(resp?.data?.message || 'Falha ao salvar');
        return;
      }

      setSuccess('Cliente atualizado com sucesso');
      setTimeout(() => navigate(`/app/clientes/${id}`), 300);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
            <Typography variant="h5">Editar Cliente</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
                Voltar
              </Button>
              <Button variant="contained" onClick={handleSalvar} disabled={saving || !form.nome.trim()}>
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Nome" value={form.nome} onChange={handleChange('nome')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={<Switch checked={form.ativo} onChange={handleChange('ativo')} />}
                label={form.ativo ? 'Ativo' : 'Inativo'}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Celular" value={form.celular} onChange={handleChange('celular')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Telefone" value={form.telefone} onChange={handleChange('telefone')} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="E-mail" value={form.email} onChange={handleChange('email')} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="CPF" value={form.cpf} onChange={handleChange('cpf')} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="RG" value={form.rg} onChange={handleChange('rg')} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Endereço" value={form.endereco} onChange={handleChange('endereco')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Bairro" value={form.bairro} onChange={handleChange('bairro')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Cidade" value={form.cidade} onChange={handleChange('cidade')} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                value={form.observacoes}
                onChange={handleChange('observacoes')}
                multiline
                minRows={4}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClienteEditar;
